import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import apiClient from '../../api/client';
import { BottomNav } from '../../components/app/DarkTheme';
import { ArrowLeft, Key, User, LogOut, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

// 한국 서비스 기준 Asia/Seoul로 날짜 표시
const formatJoinDate = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '-';
  // YYYY-MM-DD 형식 우선 사용 (KST 변환 후 YYYY.MM.DD로 표시)
  const fmt = new Intl.DateTimeFormat('ko-KR', {
    timeZone: 'Asia/Seoul',
    year: 'numeric', month: '2-digit', day: '2-digit',
  });
  return fmt.format(d).replace(/\s/g, '').replace(/\.$/, '');
};

const MemberSettings = () => {
  const navigate = useNavigate();
  const { token, member, logout, clearPinWarningFlag } = useAuth();
  const [showPinChange, setShowPinChange] = useState(false);
  const [pinForm, setPinForm] = useState({
    current_pin: '',
    new_pin: '',
    confirm_pin: ''
  });
  const [saving, setSaving] = useState(false);
  const [summary, setSummary] = useState({ joined_at: null, attendance_count: null });

  useEffect(() => {
    if (!token) return;
    let active = true;
    apiClient
      .get('/app/profile-summary', { params: { token } })
      .then((res) => { if (active) setSummary(res.data); })
      .catch(() => { /* 조용히 무시 - 카드는 placeholder로 표시 */ });
    return () => { active = false; };
  }, [token]);

  const handlePinChange = async (e) => {
    e.preventDefault();
    
    if (!/^\d{4}$/.test(pinForm.new_pin)) {
      toast.error('PIN은 4자리 숫자로 입력해주세요');
      return;
    }
    
    if (pinForm.new_pin !== pinForm.confirm_pin) {
      toast.error('새 PIN이 서로 일치하지 않습니다');
      return;
    }
    
    setSaving(true);
    try {
      await apiClient.post('/app/auth/change-pin', pinForm, {
        params: { token }
      });
      
      toast.success('PIN이 변경되었습니다');
      clearPinWarningFlag();
      setShowPinChange(false);
      setPinForm({ current_pin: '', new_pin: '', confirm_pin: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'PIN 변경 실패');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/app/login');
    toast.success('로그아웃되었습니다');
  };

  const joinDate = formatJoinDate(summary?.joined_at || member?.created_at);
  const attendanceCount = summary?.attendance_count;

  return (
    <div className="min-h-screen bg-[#0F1115] pb-24">
      {/* Header */}
      <div className="px-5 pt-12 pb-6">
        <h1 className="text-xl font-bold text-white">설정</h1>
      </div>

      <div className="px-4 space-y-3">
        {/* Profile Card */}
        <div className="bg-[#1A1D23] rounded-2xl overflow-hidden" data-testid="profile-card">
          <div className="flex items-center gap-3 px-5 py-4 border-b border-[#22252E]">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FF7A00] to-[#FF8C42] flex items-center justify-center">
              <User className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="text-white font-semibold">{member?.name}</p>
              <p className="text-[#52525B] text-xs">{member?.phone}</p>
            </div>
          </div>

          <div className="divide-y divide-[#22252E]">
            <div className="flex justify-between items-center px-5 py-3.5" data-testid="profile-name">
              <span className="text-[#71717A] text-sm">이름</span>
              <span className="text-[#A1A1AA] text-sm font-medium">{member?.name || '-'}</span>
            </div>
            <div className="flex justify-between items-center px-5 py-3.5" data-testid="profile-phone">
              <span className="text-[#71717A] text-sm">전화번호</span>
              <span className="text-[#A1A1AA] text-sm font-medium">{member?.phone || '-'}</span>
            </div>
            <div className="flex justify-between items-center px-5 py-3.5" data-testid="profile-joined">
              <span className="text-[#71717A] text-sm">가입일</span>
              <div className="text-right">
                <div className="text-[#A1A1AA] text-sm font-medium">{joinDate}</div>
                <div className="text-[#52525B] text-[11px] mt-0.5">부터 함께 운동 중</div>
              </div>
            </div>
            <div className="flex justify-between items-center px-5 py-4" data-testid="profile-attendance">
              <span className="text-[#71717A] text-sm">누적 출석</span>
              <div className="flex items-baseline gap-1">
                <span className="text-[#FF7A00] text-2xl font-bold tabular-nums tracking-tight">
                  {attendanceCount ?? '-'}
                </span>
                <span className="text-[#A1A1AA] text-sm font-medium">회</span>
              </div>
            </div>
          </div>
        </div>

        {/* PIN Change Card */}
        <div className="bg-[#1A1D23] rounded-2xl overflow-hidden" data-testid="pin-change-card">
          {!showPinChange ? (
            <button
              onClick={() => setShowPinChange(true)}
              data-testid="show-pin-change"
              className="w-full flex items-center justify-between px-5 py-4 active:bg-[#22252E] transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#22252E] flex items-center justify-center">
                  <Key className="h-5 w-5 text-[#A1A1AA]" />
                </div>
                <div className="text-left">
                  <p className="text-white text-sm font-medium">PIN 변경</p>
                  <p className="text-[#52525B] text-xs">보안을 위해 주기적으로 변경해주세요</p>
                </div>
              </div>
              <ChevronRight className="h-4 w-4 text-[#3A3F4A]" />
            </button>
          ) : (
            <div className="p-5">
              <div className="flex items-center gap-2 mb-5">
                <Key className="h-4 w-4 text-[#FF7A00]" />
                <h2 className="text-white text-sm font-semibold">PIN 변경</h2>
              </div>
              
              <form onSubmit={handlePinChange} className="space-y-3">
                <div>
                  <label className="block text-[#71717A] text-xs mb-1.5 ml-1">현재 PIN</label>
                  <input
                    type="password"
                    placeholder="현재 PIN 4자리"
                    maxLength="4"
                    value={pinForm.current_pin}
                    onChange={(e) => setPinForm({ ...pinForm, current_pin: e.target.value })}
                    required
                    data-testid="current-pin"
                    className="w-full bg-[#22252E] border border-[#2A2E38] rounded-xl px-4 py-3 text-white text-center tracking-[0.3em] placeholder-[#52525B] text-sm focus:outline-none focus:border-[#FF7A00]/50 focus:ring-1 focus:ring-[#FF7A00]/30 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-[#71717A] text-xs mb-1.5 ml-1">새 PIN</label>
                  <input
                    type="password"
                    placeholder="새 PIN 4자리"
                    maxLength="4"
                    value={pinForm.new_pin}
                    onChange={(e) => setPinForm({ ...pinForm, new_pin: e.target.value })}
                    required
                    data-testid="new-pin"
                    className="w-full bg-[#22252E] border border-[#2A2E38] rounded-xl px-4 py-3 text-white text-center tracking-[0.3em] placeholder-[#52525B] text-sm focus:outline-none focus:border-[#FF7A00]/50 focus:ring-1 focus:ring-[#FF7A00]/30 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-[#71717A] text-xs mb-1.5 ml-1">새 PIN 확인</label>
                  <input
                    type="password"
                    placeholder="새 PIN 다시 입력"
                    maxLength="4"
                    value={pinForm.confirm_pin}
                    onChange={(e) => setPinForm({ ...pinForm, confirm_pin: e.target.value })}
                    required
                    data-testid="confirm-pin"
                    className="w-full bg-[#22252E] border border-[#2A2E38] rounded-xl px-4 py-3 text-white text-center tracking-[0.3em] placeholder-[#52525B] text-sm focus:outline-none focus:border-[#FF7A00]/50 focus:ring-1 focus:ring-[#FF7A00]/30 transition-colors"
                  />
                </div>
                <div className="flex gap-2 pt-2">
                  <button
                    type="button"
                    className="flex-1 bg-[#22252E] border border-[#3A3F4A] text-[#A1A1AA] font-medium py-3 rounded-xl hover:bg-[#2A2E38] active:scale-[0.98] transition-all"
                    onClick={() => {
                      setShowPinChange(false);
                      setPinForm({ current_pin: '', new_pin: '', confirm_pin: '' });
                    }}
                  >
                    취소
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    data-testid="submit-pin-change"
                    className="flex-1 bg-gradient-to-r from-[#FF7A00] to-[#FF8C42] text-white font-semibold py-3 rounded-xl hover:brightness-110 active:scale-[0.98] disabled:opacity-50 transition-all"
                  >
                    {saving ? '변경 중...' : '변경'}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>

        {/* Logout */}
        <button
          onClick={handleLogout}
          data-testid="logout-btn"
          className="w-full bg-[#1A1D23] rounded-2xl px-5 py-4 flex items-center gap-3 active:bg-[#22252E] transition-colors"
        >
          <div className="w-10 h-10 rounded-full bg-[#EF4444]/10 flex items-center justify-center">
            <LogOut className="h-5 w-5 text-[#EF4444]" />
          </div>
          <span className="text-[#EF4444] text-sm font-medium">로그아웃</span>
        </button>
      </div>

      <BottomNav activeTab="settings" />
    </div>
  );
};

export default MemberSettings;
